import type { AnchorTarget, IconName } from '@zapier/design-system';

export type LinkIconType = {
  selected: IconName;
  unselected: IconName;
};

export type HeroArea = {
  heading: string;
  subheading: string;
  submenu: {
    description: string;
    href: string;
    target?: AnchorTarget;
    text: string;
  }[];
};

//////////////////////////////////////////////////////////////////////
// Link typings
//////////////////////////////////////////////////////////////////////

export type UniversalNavLinkType = {
  description?: string;
  href: string | null;
  icon?: IconName | LinkIconType;
  isExternal?: boolean;
  submenu?: Array<UniversalNavLinkType>;
  target?: AnchorTarget;
  text: string;
};

export type NavItemVariantA = {
  variant: 'variant-a';
  bottomLinks: Array<UniversalNavLinkType>;
  heroArea: HeroArea;
  promoArea: {
    header: {
      heading: string;
      href?: string;
      target?: AnchorTarget;
      subheading: string;
    };
    submenu: {
      description: string;
      href: string;
      target?: AnchorTarget;
      icon: string;
      text: string;
      tag?: string;
    }[];
  };
  text: string;
};

export type SharedNavItemVariant = {
  bottomLinks: Array<UniversalNavLinkType>;
  promoArea: Array<ArticleStubType | PromoLinksListType>;
  submenu: Array<UniversalNavLinkType>;
  text: string;
};

export type NavItemVariantB = SharedNavItemVariant & {
  variant: 'variant-b';
};

export type NavItemVariantC = SharedNavItemVariant & {
  variant: 'variant-c';
};

export type NavItemLink = {
  variant: 'link';
  href: string;
  target?: AnchorTarget;
  text: string;
};

export type NavItem =
  | NavItemVariantA
  | NavItemVariantB
  | NavItemVariantC
  | NavItemLink;

//////////////////////////////////////////////////////////////////////
// Sidebar links
//////////////////////////////////////////////////////////////////////

export const usageLink: UniversalNavLinkType = {
  href: '/app/settings/billing',
  icon: { selected: 'miscWallet', unselected: 'miscWallet' },
  text: 'Usage',
};

//////////////////////////////////////////////////////////////////////
// Account menu links
//////////////////////////////////////////////////////////////////////

export const moveZapsLink: UniversalNavLinkType = {
  href: '/app/move-zaps/',
  icon: { selected: 'arrowBigRight', unselected: 'arrowBigRight' },
  text: 'Move Zaps',
};

export const settingsLink: UniversalNavLinkType = {
  href: '/app/settings/profile',
  icon: { selected: 'navCog', unselected: 'navCog' },
  text: 'Settings',
};

export const logOutLink: UniversalNavLinkType = {
  href: '/logout',
  icon: 'arrowExit',
  text: 'Log Out',
};

export const AccountsMenuLinks: Array<UniversalNavLinkType> = [
  // `moveZapsLink` conditionally rendered.
  settingsLink,
  logOutLink,
];

//////////////////////////////////////////////////////////////////////
// Dynamic Topnav Data
//////////////////////////////////////////////////////////////////////

export type ArticleStubType = {
  type: string;
  text: UniversalNavLinkType['text'];
  href: UniversalNavLinkType['href'];
  imageUrl: string;
  description: UniversalNavLinkType['description'];
  altText: string;
  target?: UniversalNavLinkType['target'];
};

export type PromoLinksListType = {
  type: string;
  text?: UniversalNavLinkType['text'];
  links: Array<UniversalNavLinkType>;
};

export type CallToAction = {
  href: string;
  target?: AnchorTarget;
  text: string;
};

export type TopBarLinksType = {
  callToAction?: CallToAction;
  topBarMenu?: Array<
    NavItemVariantA | NavItemVariantB | NavItemVariantC | NavItemLink
  >;
};

export const topNavFallbackData: TopBarLinksType = {
  topBarMenu: [
    {
      heroArea: {
        heading: 'Zapier Automation Platform',
        subheading: 'No-code automation across 6,000+ apps',
        submenu: [
          {
            text: 'How it works',
            description: 'Learn the basics',
            href: '/how-it-works',
          },
          {
            text: 'Security',
            description: 'Trusted by 2M+ businesses',
            href: '/security',
          },
          {
            text: 'Features',
            description: 'Build flexible workflows',
            href: '/features',
          },
          {
            text: 'App integrations',
            description: 'Explore 6,000+ app connections',
            href: '/apps',
          },
        ],
      },
      promoArea: {
        header: {
          heading: "What's new",
          href: undefined,
          subheading: '',
        },
        submenu: [
          {
            text: 'Tables',
            href: '/tables',
            icon: 'navTable',
            description: 'No-code databases built for Zaps',
            tag: undefined,
          },
          {
            text: 'Interfaces',
            href: '/interfaces',
            icon: 'navInterfaces',
            description: 'Custom pages to power your Zaps',
            tag: 'beta',
          },
          {
            text: 'Chatbots',
            href: '/ai/chatbot',
            icon: 'miscAiChatbot',
            description: 'Easy to build, no code required',
            tag: 'beta',
          },
          {
            description: 'Access our latest AI-powered features',
            href: '/ai',
            icon: 'miscAI',
            text: 'AI features',
            tag: 'beta',
          },
        ],
      },
      text: 'Product',
      variant: 'variant-a',
      bottomLinks: [
        {
          text: 'Explore app integrations',
          icon: {
            selected: 'actionExplore',
            unselected: 'actionExplore',
          },
          href: '/apps',
        },
        {
          text: 'Join Zapier Early Access',
          icon: 'miscBoltAlt',
          href: '/early-access',
        },
      ],
    },
    {
      text: 'Solutions',
      variant: 'variant-c',
      submenu: [
        {
          text: 'By use case',
          href: null,
          icon: {
            selected: 'navBriefcase',
            unselected: 'navBriefcase',
          },
          submenu: [
            {
              text: 'Lead management',
              href: '/lead-management',
            },
            {
              text: 'Sales pipeline',
              href: '/sales-pipeline-management',
            },
            {
              text: 'Marketing campaigns',
              href: '/marketing-campaigns',
            },
            {
              text: 'Customer support',
              href: '/customer-support-management',
            },
            {
              text: 'Data management',
              href: '/data-management',
            },
            {
              text: 'Project management',
              href: '/project-management',
            },
            {
              text: 'Tickets and incidents',
              href: '/ticket-incident-management',
            },
          ],
        },
        {
          text: 'By app',
          href: null,
          icon: {
            selected: 'miscList',
            unselected: 'miscList',
          },
          submenu: [
            {
              text: 'Salesforce',
              href: '/apps/salesforce/integrations ',
            },
            {
              text: 'Microsoft Dynamics CRM',
              href: '/apps/microsoft-dynamics-crm/integrations',
            },
            {
              text: 'HubSpot',
              href: '/apps/hubspot/integrations',
            },
            {
              text: 'Marketo',
              href: '/apps/marketo/integrations',
            },
            {
              text: 'Slack',
              href: '/apps/slack/integrations',
            },
            {
              text: 'Microsoft Teams',
              href: '/apps/microsoft-teams/integrations',
            },
            {
              text: 'Zendesk',
              href: '/apps/zendesk/integrations',
            },
            {
              text: 'Jira Software Cloud',
              href: '/apps/jira-software-cloud/integrations',
            },
            {
              text: 'Jira Service Management',
              href: '/apps/jira-service-management/integrations',
            },
          ],
        },
        {
          text: 'By team',
          href: null,
          icon: {
            selected: 'personGroup',
            unselected: 'personGroup',
          },
          submenu: [
            {
              text: 'Marketing',
              href: '/solutions/marketing',
            },
            {
              text: 'Leaders',
              href: '/solutions/leaders',
            },
            {
              text: 'IT',
              href: '/solutions/it',
            },
            {
              text: 'Sales operations',
              href: '/solutions/sales',
            },
          ],
        },
        {
          text: 'By company size',
          href: null,
          icon: {
            selected: 'navExplore',
            unselected: 'navExplore',
          },
          submenu: [
            {
              text: 'Startups',
              href: '/solutions/startups',
            },
            {
              text: 'Small and medium businesses',
              href: '/solutions/smb',
            },
            {
              text: 'Enterprise',
              href: '/solutions/enterprise',
            },
          ],
        },
      ],
      bottomLinks: [
        {
          text: 'Explore app integrations',
          icon: {
            selected: 'actionExplore',
            unselected: 'actionExplore',
          },
          href: '/apps',
        },
        {
          text: 'Join Zapier Early Access',
          icon: 'miscBoltAlt',
          href: '/early-access',
        },
      ],
      promoArea: [
        {
          type: 'article-stub',
          text: 'How Zapier Zaps',
          href: '/l/lead-management-automation-canvas',
          imageUrl:
            'https://res.cloudinary.com/zapier-media/image/upload/q_auto/f_auto/v1695750563/Navigation/Logged%20Out/Frame_12120_ef7x6o.png',
          description: 'How Zapier’s RevOps team automates lead management',
          altText: 'Person icon with workflow graphic',
        },
        {
          type: 'article-stub',
          text: 'How Zapier Uses AI',
          href: '/resources/webinar/leaders-using-ai',
          imageUrl:
            'https://res.cloudinary.com/zapier-media/image/upload/q_auto/f_auto/v1695750563/Navigation/Logged%20Out/Mask_group_aprx5j.png',
          description: 'Using AI & Zapier in Marketing, Sales, & RevOps',
          altText:
            'Graphic that shows webhooks, ChatGPT, and Slack apps connected',
        },
      ],
    },
    {
      text: 'Resources & Support',
      variant: 'variant-b',
      submenu: [
        {
          text: 'By team',
          href: null,
          icon: {
            selected: 'personGroup',
            unselected: 'personGroup',
          },
          submenu: [
            {
              text: 'Marketing',
              href: '/resources/marketing',
            },
            {
              text: 'Leaders',
              href: '/resources/leaders',
            },
            {
              text: 'IT',
              href: '/resources/it',
            },
            {
              text: 'Sales operations',
              href: '/resources/sales',
            },
          ],
        },
        {
          text: 'Learn more',
          href: null,
          icon: {
            selected: 'miscLearn',
            unselected: 'miscLearn',
          },
          submenu: [
            {
              text: 'Blog',
              href: '/blog/',
            },
            {
              text: 'Zapier Learn',
              href: 'https://learn.zapier.com',
            },
            {
              text: 'Events and webinars',
              href: '/resources/events',
            },
            {
              text: 'Customer stories',
              href: '/customer-stories',
            },
            {
              text: 'Zapier guides',
              href: '/resources/guides',
            },
          ],
        },
        {
          text: 'Get help',
          href: null,
          icon: {
            selected: 'navHelp',
            unselected: 'navHelp',
          },
          submenu: [
            {
              text: 'Help Center',
              href: '/help',
            },
            {
              text: 'Community',
              href: 'https://community.zapier.com/',
            },
            {
              text: 'Hire an Expert',
              href: '/experts',
            },
            {
              text: 'Support Services',
              href: '/l/support-services',
            },
            {
              text: 'Contact Support',
              href: '/get-help',
            },
          ],
        },
      ],
      bottomLinks: [
        {
          text: 'Explore app integrations',
          icon: {
            selected: 'actionExplore',
            unselected: 'actionExplore',
          },
          href: '/apps',
        },
        {
          text: 'Join Zapier Early Access',
          icon: 'miscBoltAlt',
          href: '/early-access',
        },
      ],
      promoArea: [
        {
          type: 'article-stub',
          text: 'Zapier quick-start guide',
          href: '/learn/zapier-quick-start-guide',
          imageUrl:
            'https://cdn.zapier.com/storage/photos/8d754b5e8481fdd190dd7b1cf7738a8d.png',
          description: 'Create your first Zap with ease',
          altText:
            "A simplified user interface design representing a portion of Zapier's platform. There's a vertical menu of platform options. At the top is a dominant orange button with text inside that reads \"Create Zap.\" Beneath the button is a stack of menu icons to represent Zapier's dashboard and zaps.",
        },
        {
          type: 'link-list',
          text: 'Developer resources',
          href: null,
          links: [
            {
              text: 'Developer Platform',
              href: '/developer-platform',
            },
            {
              text: 'Build an integration',
              href: '/developer-platform/integrations',
            },
            {
              text: 'Embed an integration',
              href: '/developer-platform/embed-tools',
            },
            {
              text: 'Integration Partner Program',
              href: 'developer-platform/partner-program',
            },
            {
              text: 'Documentation',
              href: 'https://platform.zapier.com',
            },
          ],
        },
      ],
    },
    {
      variant: 'link',
      text: 'Pricing',
      href: '/pricing',
    },
  ],
  callToAction: {
    href: 'https://zapier.com/l/contact-sales',
    text: 'Contact Sales',
  },
};
